import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';

import api, { fetchProjectListApi, fetchRoleListApi, getAppliedRoleListForDrCr } from '../../services/api';
import MetaTags from '../../components/MetaTags';
import DataTable from '../../components/DataTable'; 
import ProjectsKpiGrid from '../../components/projectsPage/ProjectsKpiGrid';
import ProjectMobileCard from '../../components/projectsPage/ProjectMobileCard';
import { getProjectColumns } from '../../components/projectsPage/ProjectColumns';
import './ManageProjectPage.css';
import { useAuth } from '../../context/AuthContext';

const ManageProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const hasPermission = useCallback((moduleKey, action = 'View') => {
    if (action === 'Delete' && String(user?.roleCode || user?.role_code) === '900') return false;
    if (String(user?.roleCode || user?.role_code) === '400' || user?.role_code === '200') return true;
    return (user?.permissions || []).includes(`${moduleKey}:${action}`);
  }, [user]);

  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0, totalRoles: 0, totalAuditions: 0, selected: 0, rejected: 0, pending: 0
  });

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await fetchProjectListApi();
      const rolesRes = await fetchRoleListApi();
      const applicationsRes = await getAppliedRoleListForDrCr();

      const rolesData = rolesRes?.data?.data || [];
      const applications = applicationsRes?.data?.data || [];

      const mappedProjects = (data.data || []).map(p => {
        const currentProjectJogetId = String(p.project_joget_id || p.joget_project_id || p.projectId || '');
        const projectRoles = rolesData.filter(role => String(role.project_joget_id || role.projectId || role.c_projectid || '') === currentProjectJogetId);
        const projectRoleIds = projectRoles.map(role => String(role.joget_role_id || role.roleId || ''));
        const projectApplications = applications.filter(app => projectRoleIds.includes(String(app.joget_role_id || app.c_roleId || app.roleId || '')));

        const getResult = (app) => String(app.result || app.c_result || '').toLowerCase();
        
        return {
          id: p.id,
          projectName: p.project_name || p.projectName,
          projectType: p.project_type || p.projectType,
          projectDirectorName: p.director_name || p.projectDirectorName || "N/A",
          projectStartDate: p.start_date || p.projectStartDate,
          projectEndDate: p.end_date || p.projectEndDate,
          rolesCount: projectRoles.length,
          auditionsCount: projectApplications.length,
          selectedCount: projectApplications.filter(app => getResult(app) === 'selected').length,
          rejectedCount: projectApplications.filter(app => getResult(app) === 'rejected').length,
          pendingCount: projectApplications.filter(app => ['pending', 'resubmit', 'applied'].includes(getResult(app))).length,
          status: p.status === "active" ? "Active" : (p.status === "inactive" ? "Inactive" : "Expired")
        };
      });

      setProjects(mappedProjects);

      const stats = mappedProjects.reduce((acc, item) => ({
        totalRoles: acc.totalRoles + (item.rolesCount || 0),
        totalAuditions: acc.totalAuditions + (item.auditionsCount || 0),
        selected: acc.selected + (item.selectedCount || 0),
        rejected: acc.rejected + (item.rejectedCount || 0),
        pending: acc.pending + (item.pendingCount || 0)
      }), { totalRoles: 0, totalAuditions: 0, selected: 0, rejected: 0, pending: 0 });

      setDashboardStats({ totalProjects: mappedProjects.length, ...stats });
    } catch (err) {
      console.error(err);
      setError('Failed to load projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleEdit = useCallback((projectId) => {
    if (!projectId) {
      return Swal.fire({ icon: 'error', title: 'Error!', text: 'Project ID not found.', background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429' });
    }
    navigate(`/dashboard/post-projects/${projectId}`);
  }, [navigate]);

  const handleDelete = useCallback(async (projectOrId) => {
    const project = typeof projectOrId === 'object' ? projectOrId : projects.find(p => String(p.id) === String(projectOrId));
    if (!project?.id) {
      return Swal.fire({ icon: 'error', title: 'Error!', text: 'Invalid Project ID.', background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429' });
    }

    if ((project.rolesCount || 0) > 0 || (project.auditionsCount || 0) > 0) {
      return Swal.fire({
        icon: 'warning',
        title: 'Cannot Delete Project!',
        html: `This project currently has <b style="color: #38bdf8">${project.rolesCount || 0} Role(s)</b> and <b style="color: #f72585">${project.auditionsCount || 0} Audition Application(s)</b> active.`,
        background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429'
      });
    }

    const result = await Swal.fire({
      title: 'Are you sure?', text: `Delete project "${project.projectName}"?`, icon: 'warning',
      showCancelButton: true, background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429', cancelButtonColor: '#1E2342', confirmButtonText: 'Yes, Delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/projects/${project.id}`);
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Project deleted successfully!', background: '#0E1124', color: '#fff', confirmButtonColor: '#10B981' });
        loadProjects();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Delete Failed!', text: err.response?.data?.message || 'Delete failed.', background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429' });
      }
    }
  }, [projects, loadProjects]);

  const columns = useMemo(() => getProjectColumns({ handleEdit, handleDelete, hasPermission }), [handleEdit, handleDelete, hasPermission]);
  const renderMobileCard = useCallback((project) => (
    <ProjectMobileCard key={project.id || project._id} project={project} handleEdit={handleEdit} handleDelete={handleDelete} hasPermission={hasPermission} />
  ), [handleEdit, handleDelete, hasPermission]);

  if (loading) {
    return (
      <div className="text-center p-5 vh-100">
        <Spinner animation="border" variant="danger" />
        <p className="text-white mt-2">Project Loading...</p>
      </div>
    );
  }

  return (
    <div className="post-project-page p-2 p-md-3 animate-fade-in">
      <MetaTags title="Manage Projects - Dashboard" />
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Projects</span>
        </h5>
        {hasPermission('Projects', 'Create') && (
          <Button as={Link} to="/dashboard/post-projects" variant="danger" className="btn-compact-header btn-sm fw-bold">
            <FaPlus className="me-1" /> New Project
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}
      <ProjectsKpiGrid dashboardStats={dashboardStats} />
      <DataTable columns={columns} data={projects} renderMobileItem={renderMobileCard} searchPlaceholder="Search projects or directors..." className="form-control bg-dark text-white border-secondary w-100 w-md-auto" />
    </div>
  );
};

export default ManageProjectsPage;